import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowsClockwise, Truck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import CustomerLink from "@/components/CustomerLink";

// Default Leaflet markers ship as relative URLs that don't resolve in our bundle —
// override them once with the same artwork served from unpkg.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const REFRESH_MS = 30000; // align with Samsara cache TTL

const fmtTime = (iso) => {
  if (!iso) return "—";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(iso).toLocaleString("en-CA", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

export default function Fleet() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Default filter · 2026-08-19: revert to `all` after P0 fleet
  // regression. The 2026-08-01 P0 change defaulted to `en_route`,
  // which relies on today's Sent orders + a Fleet-vehicle→employee
  // join. When either side is thin (weekends, dispatch gap, driver
  // reassignment), the map shows the misleading empty-state
  // "No trucks are currently en route" even though trucks are
  // reporting. Restore the operator's mental model: default view
  // is everything reporting; narrow with the filter chips.
  const [filter, setFilter] = useState("all");

  const load = async (statusFilter = filter) => {
    try {
      const res = await api.get("/fleet/locations", { params: { status: statusFilter } });
      setLocations(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load truck locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
    const t = setInterval(() => load(filter), REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const center = locations.length
    ? [locations[0].lat, locations[0].lng]
    : [43.3669, -81.4855]; // Coolman yard fallback

  return (
    <div data-testid="fleet-page" className="-m-6 flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Fleet</h1>
          <p className="text-xs text-gray-500">
            Live truck locations · refreshes every 30 seconds
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FleetStatusFilter value={filter} onChange={setFilter} />
          <button
            data-testid="fleet-refresh"
            onClick={() => load(filter)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-1 items-center justify-center bg-[#f9fafb]">
          <div className="max-w-md rounded-md border border-red-200 bg-red-50/60 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        </div>
      ) : (
        <div className="relative flex-1">
          <MapContainer
            center={center}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            data-testid="fleet-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <RecenterOnData locations={locations} />
            {locations.map((t) => (
              <Marker
                key={t.name}
                position={[t.lat, t.lng]}
                data-testid={`fleet-marker-${t.name}`}
              >
                <Tooltip
                  permanent
                  direction="top"
                  offset={[0, -32]}
                  opacity={1}
                  className="truck-label"
                >
                  <VehicleLabel truck={t} />
                </Tooltip>
                <Popup>
                  <div className="space-y-0.5 text-[13px]">
                    <div className="flex items-center gap-1.5 font-semibold text-gray-900">
                      <Truck size={14} weight="fill" /> {t.name}
                    </div>
                    {t.assigned_driver_name ? (
                      <div className="text-gray-700" data-testid={`fleet-popup-driver-${t.name}`}>
                        {t.assigned_driver_name}
                      </div>
                    ) : (
                      <div className="text-[12px] italic text-gray-500" data-testid={`fleet-popup-unassigned-${t.name}`}>
                        Unassigned
                      </div>
                    )}
                    {t.active_order && (
                      <div
                        data-testid={`fleet-popup-active-order-${t.name}`}
                        className="rounded bg-sky-50 px-2 py-1 text-[13px]"
                      >
                        <div className="font-semibold text-sky-800">
                          Order #{t.active_order.order_number}
                        </div>
                        <div className="text-[12px] text-sky-700">
                          {t.active_order.delivery_address}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] uppercase tracking-wider text-sky-700">
                          <span className="rounded bg-white/70 px-1 py-0.5 font-semibold">
                            {t.active_order.delivery_status}
                          </span>
                          {t.active_order.priority && (
                            <span className="rounded bg-white/70 px-1 py-0.5 font-semibold">
                              {t.active_order.priority}
                            </span>
                          )}
                          {t.active_order.arrival_time && (
                            <span className="rounded bg-white/70 px-1 py-0.5 font-semibold">
                              ETA {t.active_order.arrival_time}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {t.at_customer && (
                      <div className="rounded bg-emerald-50 px-2 py-1 text-[13px]">
                        <div className="font-semibold text-emerald-800">
                          At: <CustomerLink
                            customerId={t.at_customer.customer_id}
                            accountNumber={t.at_customer.account_number}
                            customerName={t.at_customer.customer_name}
                            className="!text-emerald-800 hover:!text-emerald-900"
                            testId={`fleet-popup-customer-${t.name}`}
                          />
                        </div>
                        <div className="font-mono text-[11px] text-emerald-700">
                          #{t.at_customer.account_number}
                          {t.at_customer.confidence === "medium" && " · likely match"}
                        </div>
                      </div>
                    )}
                    {t.is_moving && (
                      <div className="text-xs italic text-gray-500">
                        Moving{t.speed_mph != null && ` · ${Math.round(t.speed_mph)} mph`}
                      </div>
                    )}
                    {t.address && <div className="text-gray-500">{t.address}</div>}
                    <div className="text-xs text-gray-400">
                      Last seen {fmtTime(t.last_seen)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Truck list overlay (top-left) */}
          <div className="pointer-events-none absolute left-4 top-4 z-[400] max-h-[80%] overflow-y-auto">
            <div className="pointer-events-auto w-64 rounded-md border border-gray-200 bg-white/95 p-3 shadow-md backdrop-blur">
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
                {locations.length} truck{locations.length === 1 ? "" : "s"} reporting
              </div>
              {locations.length === 0 && (
                <div data-testid="fleet-empty-state" className="rounded border border-gray-200 bg-gray-50 px-2 py-2 text-xs text-gray-600 space-y-1">
                  <div>
                    {filter === "en_route"
                      ? "No trucks are currently en route."
                      : filter === "at_yard"
                      ? "No trucks are at the yard right now."
                      : filter === "maintenance"
                      ? "No trucks are marked for maintenance."
                      : "No trucks are reporting a location."}
                  </div>
                  {filter === "en_route" ? (
                    <button
                      type="button"
                      onClick={() => setFilter("all")}
                      className="text-[11px] font-medium text-indigo-700 hover:underline"
                      data-testid="fleet-empty-show-all"
                    >
                      Show all reporting trucks &rarr;
                    </button>
                  ) : null}
                </div>
              )}
              <ul className="space-y-1.5">
                {locations.map((t) => (
                  <li
                    key={t.name}
                    data-testid={`fleet-list-${t.name}`}
                    className="text-sm"
                  >
                    <div className="font-semibold text-gray-900">{t.name}</div>
                    {t.assigned_driver_name ? (
                      <div className="text-xs text-gray-600">{t.assigned_driver_name}</div>
                    ) : (
                      <div className="text-xs italic text-gray-400">Unassigned</div>
                    )}
                    {(t.delivery_address || t.address) && (
                      <div
                        data-testid={`fleet-list-delivery-address-${t.name}`}
                        className="truncate text-xs text-gray-700"
                        title={t.delivery_address || t.address}
                      >
                        {t.delivery_address || t.address}
                      </div>
                    )}
                    {t.at_customer ? (
                      <div className="text-xs text-emerald-700">
                        <CustomerLink
                          customerId={t.at_customer.customer_id}
                          accountNumber={t.at_customer.account_number}
                          customerName={t.at_customer.customer_name}
                          className="!text-emerald-700 hover:!text-emerald-900"
                          testId={`fleet-list-customer-${t.name}`}
                        />
                        {t.at_customer.confidence === "medium" && (
                          <span className="ml-1 text-gray-400">(?)</span>
                        )}
                      </div>
                    ) : t.is_moving ? (
                      <div className="text-xs italic text-gray-500">en route</div>
                    ) : null}
                    {t.active_order?.order_number && (
                      <div
                        data-testid={`fleet-list-order-${t.name}`}
                        className="text-[11px] font-mono text-sky-700"
                      >
                        #{t.active_order.order_number}
                      </div>
                    )}
                    <div className="text-xs text-gray-400">{fmtTime(t.last_seen)}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecenterOnData({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length === 0) return;
    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
  }, [locations, map]);
  return null;
}


/**
 * VehicleLabel — the map tooltip that hovers above each truck marker.
 *
 * Line order (fixed, per V1.1.1 spec):
 *   1. Truck Number
 *   2. Delivery Address     ← the "where" — primary location identifier
 *   3. Customer Name        ← the "who" — secondary
 *
 * Future-ready: `additionalLines` is a data-driven array so Order Number,
 * Delivery Status, and Arrival Time can be added without redesigning the
 * label. Any renderer that wants to show a new line just pushes an
 * `{ testid, text, tone }` object — no CSS or layout changes required.
 */
function VehicleLabel({ truck: t }) {
  const deliveryAddress = t.delivery_address || t.address || null;
  const customerName = t.at_customer?.customer_name || null;
  const lowConfidence = t.at_customer?.confidence === "medium";

  // Future-ready extra lines. Enabled today for Order # + Status when the
  // truck has a matched active dispatch — additional lines (Arrival Time,
  // route ETA, etc.) drop in here without touching the layout.
  const additionalLines = [];
  if (t.active_order?.order_number) {
    additionalLines.push({
      testid: `fleet-order-${t.name}`,
      text: `#${t.active_order.order_number}`,
      tone: "order",
    });
  }
  if (t.active_order?.delivery_status && t.active_order.delivery_status !== "Sent") {
    additionalLines.push({
      testid: `fleet-status-${t.name}`,
      text: t.active_order.delivery_status,
      tone: "status",
    });
  }
  if (t.active_order?.arrival_time) {
    additionalLines.push({
      testid: `fleet-eta-${t.name}`,
      text: `ETA ${t.active_order.arrival_time}`,
      tone: "eta",
    });
  }

  return (
    <div
      data-testid={`fleet-label-${t.name}`}
      className="text-center leading-tight"
    >
      <div data-testid={`fleet-truck-${t.name}`} className="truck-name">
        {t.name}
      </div>

      {deliveryAddress && (
        <div
          data-testid={`fleet-delivery-address-${t.name}`}
          className="delivery-address"
          title={deliveryAddress}
        >
          {deliveryAddress}
        </div>
      )}

      {customerName ? (
        <div
          data-testid={`fleet-at-customer-${t.name}`}
          className={`at-customer${lowConfidence ? " medium" : ""}`}
        >
          {customerName}
        </div>
      ) : t.is_moving ? (
        <div className="en-route">en route</div>
      ) : null}

      {additionalLines.length > 0 && (
        <div className="mt-0.5 flex flex-wrap items-center justify-center gap-1">
          {additionalLines.map((line, idx) => (
            <span
              key={`${line.testid}-${idx}`}
              data-testid={line.testid}
              className={`extra-line extra-line--${line.tone}`}
            >
              {line.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * FleetStatusFilter — Dispatch-status filter tabs (P0 · 2026-08-01).
 *
 * The Fleet page's core question is "who is working right now?", not
 * "where did every GPS device last ping?". Default filter is
 * `en_route`. Yard/maintenance/all remain one click away.
 */
const FILTER_OPTIONS = [
  { value: "en_route",    label: "En Route",    testid: "fleet-filter-en-route" },
  { value: "at_yard",     label: "At Yard",     testid: "fleet-filter-at-yard" },
  { value: "maintenance", label: "Maintenance", testid: "fleet-filter-maintenance" },
  { value: "all",         label: "All",         testid: "fleet-filter-all" },
];

function FleetStatusFilter({ value, onChange }) {
  return (
    <div className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5" data-testid="fleet-status-filter">
      {FILTER_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          data-testid={opt.testid}
          onClick={() => onChange(opt.value)}
          className={`px-2.5 py-1 text-xs font-medium rounded ${
            value === opt.value
              ? "bg-gray-900 text-white"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
